import { useEffect, useRef } from 'react';
import { Animated, Easing, Platform } from 'react-native';
import { SQUAD_TV_SCROLL_PIXELS_PER_SECOND } from '../constants/squadTvMode.constants';

interface UseSquadTvMarqueeScrollParams {
  enabled: boolean;
  /** Number of rows in a single (un-duplicated) column. */
  rowCount: number;
  /** Fixed pixel height of one row. */
  rowHeight: number;
  /** Measured height of the column viewport (from onLayout). */
  viewportHeight: number;
  pixelsPerSecond?: number;
}

interface UseSquadTvMarqueeScrollResult {
  translateY: Animated.Value;
  /** True when the list is tall enough to scroll; rows are duplicated for a seamless loop. */
  isScrolling: boolean;
}

/**
 * Continuous credits-style upward scroll for a Stadium-board column.
 * The caller renders the rows twice; this animates translateY from 0 to
 * -singleHeight on a linear loop, so the second copy lands exactly where the
 * first began and the reset is seamless. No per-row re-renders — one transform.
 */
export function useSquadTvMarqueeScroll({
  enabled,
  rowCount,
  rowHeight,
  viewportHeight,
  pixelsPerSecond = SQUAD_TV_SCROLL_PIXELS_PER_SECOND,
}: UseSquadTvMarqueeScrollParams): UseSquadTvMarqueeScrollResult {
  const translateYRef = useRef(new Animated.Value(0));
  const singleHeight = rowCount * rowHeight;
  const isScrolling =
    enabled && rowCount > 0 && viewportHeight > 0 && singleHeight > viewportHeight;

  useEffect(() => {
    const translateY = translateYRef.current;
    translateY.setValue(0);

    if (!isScrolling) return;

    const duration = (singleHeight / pixelsPerSecond) * 1000;
    const animation = Animated.loop(
      Animated.timing(translateY, {
        toValue: -singleHeight,
        duration,
        easing: Easing.linear,
        useNativeDriver: Platform.OS !== 'web',
      }),
    );
    animation.start();

    return () => {
      animation.stop();
      translateY.setValue(0);
    };
  }, [isScrolling, singleHeight, pixelsPerSecond]);

  return { translateY: translateYRef.current, isScrolling };
}
